import { useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Template } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const CHANNELS = ['WHATSAPP', 'SMS', 'EMAIL', 'PUSH'] as const;
const LANGS = ['en', 'fr', 'crs'] as const;

export function Templates() {
  const { persona } = useAuth();
  const canRead = (persona?.roles.includes('TEMPLATE_ADMIN') || persona?.roles.includes('BROADCASTER')) ?? false;
  const canAdmin = persona?.roles.includes('TEMPLATE_ADMIN') ?? false;
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (q) p.set('q', q);
    return `/api/v1/notify/templates?${p}`;
  }, [q]);
  const listQ = useApiQuery<{ items: Template[] }>(canRead ? query : null);

  const approveWa = async (t: Template) => {
    // eslint-disable-next-line no-alert
    const waTemplateName = window.prompt('WABA-approved template name (as registered with Meta):', t.code);
    if (!waTemplateName) return;
    setActionError(null);
    try {
      await api.post(`/api/v1/notify/templates/${t.id}/approve-wa`, { waTemplateName });
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approval failed');
    }
  };

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Template studio" subtitle="Notification templates per channel and language" />
        <EmptyState title="No access" hint="This screen needs the TEMPLATE_ADMIN or BROADCASTER role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Template studio"
        subtitle="Notification templates per channel and language, plus WhatsApp WABA approval tracking"
        actions={
          canAdmin ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New template
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      <div className="mb-4">
        <input className="field-input max-w-xs" placeholder="Search code…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No templates yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Module</th>
                <th className="table-head">Channel</th>
                <th className="table-head">Lang</th>
                <th className="table-head">Body</th>
                <th className="table-head">WABA</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{t.code}</td>
                  <td className="table-cell text-xs">{t.moduleCode}</td>
                  <td className="table-cell text-xs">{t.channel}</td>
                  <td className="table-cell text-xs uppercase">{t.lang}</td>
                  <td className="table-cell max-w-sm truncate text-xs text-slate-500" title={t.body}>
                    {t.body}
                  </td>
                  <td className="table-cell">
                    {t.channel === 'WHATSAPP' ? (
                      t.approvedAt ? (
                        <StatusBadge status="APPROVED" />
                      ) : (
                        <StatusBadge status="PENDING" />
                      )
                    ) : (
                      <span className="text-xs text-slate-300">n/a</span>
                    )}
                  </td>
                  <td className="table-cell text-right">
                    {canAdmin ? (
                      <div className="flex justify-end gap-1.5">
                        <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setEditing(t)}>
                          Edit
                        </button>
                        {t.channel === 'WHATSAPP' && !t.approvedAt ? (
                          <button className="btn-success !px-2 !py-0.5 text-xs" onClick={() => approveWa(t)}>
                            Approve WABA
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {editing ? (
        <EditModal
          template={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState<{
    code: string;
    moduleCode: string;
    channel: (typeof CHANNELS)[number];
    lang: (typeof LANGS)[number];
    body: string;
  }>({ code: '', moduleCode: '', channel: 'SMS', lang: 'en', body: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/notify/templates', form);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New template" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code (module.event)">
            <input
              className="field-input"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="c13.role_grant_pending"
            />
          </Field>
          <Field label="Owning module code">
            <input
              className="field-input"
              value={form.moduleCode}
              onChange={(e) => setForm({ ...form, moduleCode: e.target.value })}
              placeholder="c13"
            />
          </Field>
          <Field label="Channel">
            <select
              className="field-input"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value as (typeof CHANNELS)[number] })}
            >
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Language">
            <select
              className="field-input"
              value={form.lang}
              onChange={(e) => setForm({ ...form, lang: e.target.value as (typeof LANGS)[number] })}
            >
              {LANGS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Body ({{variables}})">
          <textarea className="field-input" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !form.code || !form.moduleCode || !form.body}>
            {busy ? 'Saving…' : 'Save template'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EditModal({ template, onClose, onDone }: { template: Template; onClose: () => void; onDone: () => void }) {
  const [body, setBody] = useState(template.body);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/notify/templates/${template.id}`, { body });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Edit ${template.code} (${template.channel}/${template.lang})`} onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {template.channel === 'WHATSAPP' ? (
          <p className="text-xs text-amber-600">Editing a WhatsApp template body clears its WABA approval — it will need re-approval.</p>
        ) : null}
        <Field label="Body">
          <textarea className="field-input" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || body.trim().length < 3}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
